// API Configuration and Helper
// Use environment variable or default to localhost for development
import { toast } from 'sonner';

export const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:8000';

const api = {
  setToken(token) {
    if (token) {
      localStorage.setItem('token', token);
    } else {
      localStorage.removeItem('token');
    }
  },
  
  getToken() {
    return localStorage.getItem('token');
  },
  
  async request(endpoint, options = {}) {
    const isFormData = options.body instanceof FormData;
    const token = this.getToken();
    const headers = {
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      ...options.headers
    };
    
    // Don't set Content-Type for FormData, let the browser set it with boundary
    if (!isFormData) {
      headers['Content-Type'] = 'application/json';
    }
    
    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers
    });
    
    if (response.status === 401) {
      this.setToken(null);
      window.location.href = '/login';
      throw new Error('Unauthorized');
    }

    // 403 used to fall through to the generic !response.ok branch below and
    // get swallowed unless a caller happened to catch and inspect it. A
    // permission-denied response deserves visible, consistent feedback
    // regardless of which screen triggered it — not a silent failure, and
    // not a hard redirect like 401 (the user is authenticated, just not
    // allowed to do this one thing).
    if (response.status === 403 && !options.raw) {
      let detail = 'You do not have permission to do that.';
      try { detail = (await response.clone().json())?.detail || detail; } catch (_) { /* not JSON */ }
      toast.error(detail);
      const error = new Error(detail);
      error.status = 403;
      throw error;
    }

    // Raw mode — for file downloads (blobs/zips/PDFs) where the caller needs
    // the Response itself rather than JSON-parsed data.
    if (options.raw) {
      if (!response.ok) {
        let detail = 'Request failed';
        try { detail = (await response.json())?.detail || detail; } catch (_) { /* not JSON */ }
        throw new Error(detail);
      }
      return response;
    }

    let data = null;
    const contentType = response.headers.get('Content-Type') || '';

    if (response.status !== 204 && contentType.includes('application/json')) {
      try {
        data = await response.json();
      } catch (err) {
        // In case the response body has already been read or is empty
        data = null;
      }
    }

    if (!response.ok) {
      const errorMessage = data?.detail || data?.message || 'Request failed';
      const error = new Error(errorMessage);
      error.status = response.status;
      throw error;
    }

    return data;
  },
  
  // Build a query string from params, excluding empty/null/undefined values
  _withParams(endpoint, params) {
    if (!params || Object.keys(params).length === 0) return endpoint;
    const filteredParams = {};
    Object.entries(params).forEach(([key, value]) => {
      if (value !== '' && value !== null && value !== undefined) {
        filteredParams[key] = value;
      }
    });
    if (Object.keys(filteredParams).length === 0) return endpoint;
    const queryString = new URLSearchParams(filteredParams).toString();
    return `${endpoint}?${queryString}`;
  },

  get(endpoint, options = {}) {
    const { params, ...restOptions } = options;
    return this.request(this._withParams(endpoint, params), restOptions);
  },

  post(endpoint, body, options = {}) {
    const { params, ...restOptions } = options;
    return this.request(this._withParams(endpoint, params), {
      method: 'POST',
      ...restOptions,
      body: body instanceof FormData ? body : JSON.stringify(body || {})
    });
  },

  put(endpoint, body, options = {}) {
    const { params, ...restOptions } = options;
    return this.request(this._withParams(endpoint, params), {
      method: 'PUT',
      ...restOptions,
      body: body instanceof FormData ? body : JSON.stringify(body || {})
    });
  },

  patch(endpoint, body, options = {}) {
    const { params, ...restOptions } = options;
    return this.request(this._withParams(endpoint, params), {
      method: 'PATCH',
      ...restOptions,
      body: body instanceof FormData ? body : JSON.stringify(body || {})
    });
  },

  delete(endpoint, options = {}) {
    const { params } = options;
    return this.request(this._withParams(endpoint, params), {
      method: 'DELETE'
    });
  }
};

export default api;
